import * as React from "react"
import { useStaticQuery, graphql } from "gatsby"
import { buildOrganization, buildWebSite } from "../utils/structuredData"
import { linkResolver } from "../utils/linkResolver"
import { defaultLanguage, rtlLanguages } from "../../prismic-configuration"

// Netlify serves every page at its trailing-slash URL (/epos/), so canonical
// and hreflang URLs must match that exact form to avoid pointing at redirects.
const withTrailingSlash = path => {
  if (!path) return "/"
  const clean = path.split(/[?#]/)[0]
  return clean.endsWith("/") ? clean : `${clean}/`
}

// Prismic "en-gb" -> BCP 47 "en-GB" (html lang, hreflang)
const toLangTag = lang => {
  const [language, region] = (lang || defaultLanguage).split("-")
  return region ? `${language}-${region.toUpperCase()}` : language
}

const Seo = ({
  companyName,
  title,
  description,
  keywords,
  pathname,
  children,
  author,
  lang,
  schema,
  alternateLanguages,
  image,
  imageAlt,
}) => {
  const { site } = useStaticQuery(graphql`
    query SiteQuery {
      site {
        siteMetadata {
          companyName
          title
          siteUrl
          description
          keywords {
            keyword
          }
          twitterUsername
          lang
          image
          imageAlt
        }
      }
    }
  `)

  function combineAndRemoveDuplicates(arr1 = [], arr2 = []) {
    const combinedArray = arr1.concat(arr2)
    const uniqueKeywords = new Set()
    const uniqueArray = combinedArray.filter(item => {
      if (!uniqueKeywords.has(item.keyword)) {
        uniqueKeywords.add(item.keyword)
        return true
      }
      return false
    })
    return uniqueArray
  }

  const seo = {
    companyName: companyName || site.siteMetadata.companyName,
    title: title || site.siteMetadata.title,
    description: description || site.siteMetadata.description,
    keywords: combineAndRemoveDuplicates(keywords, site.siteMetadata.keywords),
    url: `${site.siteMetadata.siteUrl}${withTrailingSlash(pathname)}`,
    author: author,
    twitterUsername: site.siteMetadata.twitterUsername,
    lang: lang || site.siteMetadata.lang,
    // Pages may pass their own absolute image URL; both it and the default
    // (static/og-image.png) are 1200×630.
    image: image || `${site.siteMetadata.siteUrl}${site.siteMetadata.image}`,
    imageAlt: imageAlt || site.siteMetadata.imageAlt,
  }
  const langTag = toLangTag(seo.lang)
  const isRTL = rtlLanguages.includes(seo.lang.toLowerCase())

  // hreflang: this page plus each Prismic translation, with x-default on the
  // English version. Only emitted when a translation exists.
  const hreflangs = alternateLanguages?.length
    ? [{ lang: seo.lang, url: seo.url }]
        .concat(
          alternateLanguages.map(alt => ({
            lang: alt.lang,
            url: `${site.siteMetadata.siteUrl}${withTrailingSlash(linkResolver(alt))}`,
          }))
        )
    : []
  const xDefault = hreflangs.find(h => h.lang === defaultLanguage)

  // JSON-LD structured data for AI engines & search.
  // Organization + WebSite ship on every page; `schema` adds page-specific
  // nodes (product catalog, FAQPage, breadcrumbs, etc.).
  const pageSchemas = (Array.isArray(schema) ? schema : schema ? [schema] : [])
    .filter(Boolean)
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [buildOrganization(), buildWebSite(), ...pageSchemas],
  }

  return (
    <>
      <html lang={langTag} dir={isRTL ? "rtl" : "ltr"} />
      <title>{`${seo.title} | ${seo.companyName}`}</title>
      <meta name="description" content={seo.description} />
      <meta
        name="keywords"
        content={seo.keywords
          .map(keyword => {
            return keyword.keyword + ", "
          })
          .join("")
          .slice(0, -2)}
      ></meta>
      <link rel="canonical" href={seo.url} />
      {hreflangs.map(h => (
        <link
          key={h.lang}
          rel="alternate"
          hrefLang={toLangTag(h.lang)}
          href={h.url}
        />
      ))}
      {xDefault && (
        <link rel="alternate" hrefLang="x-default" href={xDefault.url} />
      )}
      <meta name="image" content={seo.image} />
      <meta name="author" content={seo.author} />

      <meta property="og:type" content="website" />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={seo.imageAlt} />
      <meta property="og:site_name" content={seo.companyName} />
      <meta property="og:locale" content={langTag.replace("-", "_")} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:url" content={seo.url} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      <meta name="twitter:image:alt" content={seo.imageAlt} />
      <meta name="twitter:site" content={seo.twitterUsername} />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      {children}
    </>
  )
}

export default Seo
